<script setup lang="ts">
import { VForm } from 'vuetify/components/VForm'
import { useHead } from '@unhead/vue'
import authV1BottomShape from '@images/svg/auth-v1-bottom-shape.svg?raw'
import authV1TopShape from '@images/svg/auth-v1-top-shape.svg?raw'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import AuthBrandHeader from '@/components/auth/AuthBrandHeader.vue'
import { Modules } from '@/types/generated'
import NewPasswordData = Modules.User.NewPasswordData

const { t } = useI18n()

definePage({
  meta: {
    public: true,
    layout: 'blank',
  },
})

const route = useRoute<'password-reset-token'>()
const title = import.meta.env.VITE_APP_RU === 'only' ? 'base_title_ru' : 'base_title'

useHead({
  title: `${t(title)} - ${t('auth.forgot_title')}`,
})

const formInProcess = ref<boolean>(false)
const responseText = ref<string>('')
const tooManyRequests = ref<boolean>(false)
const isPasswordVisible = ref<boolean>(false)

const errors = ref<Record<string, string | undefined>>({
  email: undefined,
  token: undefined,
  password: undefined,
})

const refVForm = ref<VForm>()

const form = ref<NewPasswordData>({
  token: route.params.token,
  email: String(route.query.email),
  password: '',
  password_confirmation: '',
})

const submit = async () => {
  try {
    const res = await $api('/auth/new-password', {
      method: 'POST',
      body: {
        email: form.value.email,
        token: form.value.token,
        password: form.value.password,
        password_confirmation: form.value.password_confirmation,
      },
      onResponseError({ response }) {
        errors.value = response._data.errors
        if (response.status === 429) {
          tooManyRequests.value = true
          responseText.value = ''
        }
      },
    })

    responseText.value = res.status
    errors.value.email = ''
    formInProcess.value = false
    tooManyRequests.value = false
  }
  catch (err) {
    formInProcess.value = false
    console.error(err)
  }
}

const onSubmit = () => {
  tooManyRequests.value = false

  refVForm.value?.validate()
    .then(({ valid: isValid }) => {
      if (isValid) {
        formInProcess.value = true
        submit()
      }
    })
}
</script>

<template>
  <div class="auth-wrapper d-flex align-center justify-center pa-4">
    <div class="position-relative my-sm-16">
      <VNodeRenderer
        :nodes="h('div', { innerHTML: authV1TopShape })"
        class="text-primary auth-v1-top-shape d-none d-sm-block"
      />

      <VNodeRenderer
        :nodes="h('div', { innerHTML: authV1BottomShape })"
        class="text-primary auth-v1-bottom-shape d-none d-sm-block"
      />

      <VCard
        class="auth-card pa-4"
        max-width="448"
      >
        <VCardItem class="justify-center">
          <AuthBrandHeader />
        </VCardItem>

        <VCardText>
          <h4 class="text-h4 mb-1">
            {{ t('auth.new_password_title') }}
          </h4>

          <VAlert
            v-if="responseText !== ''"
            density="compact"
            color="primary"
            variant="tonal"
            class="mb-4"
          >
            {{ responseText }}
          </VAlert>
          <VAlert
            v-if="tooManyRequests"
            density="compact"
            color="warning"
            variant="tonal"
            class="mb-4"
          >
            {{ t('auth.verify_spam') }}
          </VAlert>
          <VCol
            v-if="responseText !== ''"
            cols="12"
            class="text-center text-base"
          >
            <RouterLink
              class="text-primary ms-2"
              :to="{ name: 'login' }"
            >
              {{ t('auth.login') }}
            </RouterLink>
          </VCol>
          <VForm
            v-if="responseText === ''"
            ref="refVForm"
            @submit.prevent="onSubmit"
          >
            <VRow>
              <!-- email -->
              <VCol cols="12">
                <AppTextField
                  v-model="form.email"
                  label="Email"
                  readonly="readonly"
                  type="email"
                  placeholder="johndoe@email.com"
                  :rules="[requiredValidator, emailValidator]"
                  :error-messages="errors?.email"
                />
              </VCol>

              <!-- password -->
              <VCol cols="12">
                <AppTextField
                  v-model="form.password"
                  :placeholder="t('auth.enter_password')"
                  :label="t('auth.new_password')"
                  autocomplete="off"
                  :rules="[requiredValidator, passwordValidator]"
                  :type="isPasswordVisible ? 'text' : 'password'"
                  :append-inner-icon="isPasswordVisible ? 'tabler-eye-off' : 'tabler-eye'"
                  @click:append-inner="isPasswordVisible = !isPasswordVisible"
                />
              </VCol>
              <!-- password -->
              <VCol cols="12">
                <AppTextField
                  v-model="form.password_confirmation"
                  :placeholder="t('auth.confirm_password')"
                  autocomplete="off"
                  :rules="[requiredValidator, confirmedValidator(form.password_confirmation, form.password)]"
                  :type="isPasswordVisible ? 'text' : 'password'"
                  :append-inner-icon="isPasswordVisible ? 'tabler-eye-off' : 'tabler-eye'"
                  @click:append-inner="isPasswordVisible = !isPasswordVisible"
                />
              </VCol>

              <VCol cols="12">
                <VBtn
                  block
                  :disabled="formInProcess"
                  :loading="formInProcess"
                  type="submit"
                >
                  {{ t('auth.change_password') }}
                </VBtn>
              </VCol>

              <VCol
                cols="12"
                class="text-center text-base"
              >
                <span>{{ t('auth.first_time') }}</span>
                <RouterLink
                  class="text-primary ms-2"
                  :to="{ name: 'login' }"
                >
                  {{ t('auth.login') }}
                </RouterLink>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </div>
  </div>
</template>

<style lang="scss">
@use "@core-scss/template/pages/page-auth.scss";
</style>
